import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getChat, generateInviteLink, reactivateChat, runReport } from '../api/client';
import { TierBadge } from '../components/TierBadge';
import { StatusBadge } from '../components/StatusBadge';
import { formatPosts, formatUSD, timeAgo } from '../utils/format';
import type { ChatDetail, ChatEngagement, StripeTransaction, ChatAdmin, LatestReport } from '../types';
import { DangerZone } from '../components/DangerZone';

export function ChatDetailPage() {
  const { chatId } = useParams<{ chatId: string }>();

  const { data, isLoading, error } = useQuery({
    queryKey: ['chat', chatId],
    queryFn: () => getChat(chatId!),
    enabled: !!chatId
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="chat-detail-loading">
        <div className="h-8 w-8 rounded-full border-4 border-primary-200 border-t-primary-600 animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        Failed to load chat: {error instanceof Error ? error.message : 'Unknown error'}
      </div>
    );
  }

  return (
    <div data-testid="chat-detail-page">
      <Link to="/chats" className="text-sm text-primary-700 hover:underline">
        ← Back to chats
      </Link>

      {/* Header */}
      <div className="mt-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">
            {data.projectName || <span className="text-gray-400 italic">Unnamed</span>}
          </h1>
          {data.telegramTitle && data.telegramTitle !== data.projectName && (
            <p className="text-sm text-gray-600">{data.telegramTitle}</p>
          )}
          <p className="mt-1 font-mono text-xs text-gray-500" data-testid="chat-id-display">
            {data.chatId} · {data.chatType}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <TierBadge tier={data.tier} />
          <StatusBadge status={data.status} />
          {data.exemption && (
            <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
              exempt
            </span>
          )}
          {data.telegramLink ? (
            <a
              href={data.telegramLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 rounded-md border border-sky-200 bg-sky-50 px-2.5 py-1 text-xs font-medium text-sky-700 hover:bg-sky-100"
              data-testid="open-in-telegram"
              title={data.telegramUsername ? `t.me/${data.telegramUsername}` : 'Open via t.me/c/ deep link'}
            >
              Open in Telegram ↗
            </a>
          ) : (
            <InviteLinkButton chatId={data.chatId} botMembership={data.botMembership} />
          )}
          <RunReportButton chatId={data.chatId} hasFilter={!!data.engagement.hasFilter} />
        </div>
      </div>

      {data.description && (
        <p className="mt-3 max-w-2xl text-sm text-gray-600">{data.description}</p>
      )}

      {/* Engagement summary — 'is this chat using the bot?' at a glance */}
      <EngagementSummary engagement={data.engagement} chat={data} />

      {/* Latest report — image + text + did it actually land in Telegram? */}
      {data.latestReport && <LatestReportPanel report={data.latestReport} />}

      {/* Stat strip */}
      <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4" data-testid="chat-stats">
        <Stat label="Posts MTD" value={formatPosts(data.postsMtd)} />
        <Stat
          label={data.tier === 'PRO_CREDITS' ? 'Credits left' : 'Spend MTD'}
          value={
            data.tier === 'PRO_CREDITS'
              ? formatPosts(data.remainingCredits)
              : formatUSD(data.postsMtd * 0.005)
          }
        />
        <Stat
          label="Reports last 30d"
          value={String(data.engagement.reportsLast30d)}
        />
        <Stat
          label="Last report"
          value={data.engagement.lastReportAt ? timeAgo(data.engagement.lastReportAt) : 'never'}
        />
      </div>

      {/* Two-column layout: filter+schedule on left, admins+relays on right */}
      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Section title="X API query" testid="section-filter-query">
          {data.filterQuery ? (
            <pre className="overflow-x-auto rounded bg-gray-50 p-3 text-xs font-mono text-gray-800 whitespace-pre-wrap break-words">
              {data.filterQuery}
            </pre>
          ) : (
            <p className="text-sm italic text-gray-400">No filter set yet.</p>
          )}
          <p className="mt-3 text-xs text-gray-400">
            Sent verbatim to <code className="rounded bg-gray-100 px-1">api.twitter.com/2/tweets/search/all</code>{' '}
            on every fetch. Build via <code>/setup → 🎯 Filters</code>.
          </p>
          {data.namedFilters.length > 0 && (
            <div className="mt-3 text-xs text-gray-500">
              <p className="font-medium text-gray-700">Named filters:</p>
              <ul className="mt-1 list-disc pl-5">
                {data.namedFilters.map((f) => (
                  <li key={f.name}>
                    <span className="font-mono">{f.name}</span> — {f.expression}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Section>

        <Section title={`Admins (${data.admins.length})`} testid="section-admins">
          {data.admins.length === 0 ? (
            <p className="text-sm italic text-gray-400">
              No designated notification admins. Group owners receive admin DMs by default.
            </p>
          ) : (
            <ul className="space-y-2">
              {data.admins.map((a) => (
                <AdminRow key={a.id} admin={a} />
              ))}
            </ul>
          )}
          <p className="mt-3 text-xs text-gray-400">
            From <code className="rounded bg-gray-100 px-1">SETTINGS.NOTIFICATION_ADMINS</code>;
            usernames resolved live via Telegram <code>getChatMember</code>.
          </p>
        </Section>

        <Section title="Schedule" testid="section-schedule">
          <dl className="grid grid-cols-3 gap-2 text-sm">
            <dt className="col-span-1 text-gray-500">Cron</dt>
            <dd className="col-span-2 font-mono text-xs text-gray-800">
              {data.scheduleCron || '—'}
            </dd>
            <dt className="col-span-1 text-gray-500">Human</dt>
            <dd className="col-span-2 text-gray-800">
              {data.scheduleHuman || '—'}
            </dd>
            <dt className="col-span-1 text-gray-500">Rule name</dt>
            <dd className="col-span-2 font-mono text-[11px] text-gray-700 break-all">
              {data.scheduleRuleName || '—'}
            </dd>
          </dl>
          {!data.scheduleCron && <ReactivateBanner chatId={data.chatId} />}
        </Section>

        <Section title="Period" testid="section-period">
          <dl className="grid grid-cols-3 gap-2 text-sm">
            <dt className="col-span-1 text-gray-500">XPeriod</dt>
            <dd className="col-span-2 font-mono text-xs text-gray-800">
              {data.xPeriod || <span className="italic text-gray-400">unset (default 7 days)</span>}
            </dd>
            <dt className="col-span-1 text-gray-500">Window</dt>
            <dd className="col-span-2 text-gray-800">
              {data.periodHuman || '—'}
            </dd>
          </dl>
          <p className="mt-2 text-xs text-gray-500">
            The data window every scheduled fire pulls from the X API. Edit via{' '}
            <code className="rounded bg-gray-100 px-1">/setup → Period</code> in the chat itself.
          </p>
        </Section>

        <Section title={`Auto-relays (${data.relays.length})`} testid="section-relays">
          {data.relays.length === 0 ? (
            <p className="text-sm italic text-gray-400">No relays configured.</p>
          ) : (
            <ul className="space-y-2">
              {data.relays.map((r) => (
                <li
                  key={r.username}
                  className="flex items-center justify-between rounded border border-gray-100 bg-gray-50 px-3 py-2 text-sm"
                >
                  <span className="font-mono text-gray-800">@{r.username}</span>
                  <span className="text-xs text-gray-500">
                    {r.enabled ? '▶' : '⏸'} {r.postsToday}/{r.max} today
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>

      {/* Recent reports — full-width */}
      <Section
        title={`Recent reports (${data.recentReports.length})`}
        testid="section-recent-reports"
        className="mt-6"
      >
        {data.recentReports.length === 0 ? (
          <p className="text-sm italic text-gray-400">No reports yet.</p>
        ) : (
          <ul className="space-y-1.5 text-xs">
            {data.recentReports.map((r) => {
              // Tri-state: explicit success ✓ / explicit failure ✗ /
              // legacy unstructured "?". Legacy rows pre-2026-05-04
              // don't have POST_COUNT / PERIOD / ERROR fields, so we
              // can't claim success or failure honestly.
              const successIcon = r.success === true ? '✓' : r.success === false ? '✗' : '?';
              const successColor =
                r.success === true ? 'text-emerald-700'
                : r.success === false ? 'text-red-700'
                : 'text-gray-400';
              const successTitle =
                r.success === true ? 'success'
                : r.success === false ? `failed${r.error ? ` — ${r.error}` : ''}`
                : 'legacy row — metadata unavailable';
              return (
              <li
                key={r.ts}
                className="grid grid-cols-12 items-center gap-3 rounded bg-gray-50 px-3 py-1.5"
              >
                <span className={`col-span-1 ${successColor}`} title={successTitle}>
                  {successIcon}
                </span>
                <span className="col-span-4 font-mono text-gray-700">
                  {new Date(r.ts).toISOString().slice(0, 16).replace('T', ' ')} UTC
                </span>
                <span className="col-span-3 text-gray-500">{r.period ?? '—'}</span>
                <span className="col-span-3 text-right font-medium text-gray-800">
                  {r.postCount != null ? `${r.postCount} posts` : '—'}
                </span>
                <span className="col-span-1 text-right text-[10px] text-gray-400">
                  {timeAgo(r.ts)}
                </span>
              </li>
              );
            })}
          </ul>
        )}
      </Section>

      {/* Stripe subscription summary */}
      {data.subscription && (
        <Section title="Active subscription" testid="section-subscription" className="mt-6">
          <SubscriptionDetails subscription={data.subscription} />
        </Section>
      )}

      {/* Stripe transactions */}
      <Section
        title={`Transactions (${data.transactions.length})`}
        testid="section-transactions"
        className="mt-6"
      >
        {!data.stripeCustomerId ? (
          <p className="text-sm italic text-gray-400">
            No Stripe customer linked — chat hasn't paid for anything yet.
          </p>
        ) : data.transactions.length === 0 ? (
          <p className="text-sm italic text-gray-400">
            Customer <code className="rounded bg-gray-100 px-1 font-mono text-xs">{data.stripeCustomerId}</code>{' '}
            exists but Stripe returned no charges or invoices.
          </p>
        ) : (
          <TransactionsTable transactions={data.transactions} customerId={data.stripeCustomerId} />
        )}
        <p className="mt-3 text-xs text-gray-400">
          Combined feed of <code className="rounded bg-gray-100 px-1">stripe.charges.list</code>{' '}
          (one-shot CREDITS purchases) and <code className="rounded bg-gray-100 px-1">stripe.invoices.list</code>{' '}
          (subscription billing cycles), sorted newest-first.
        </p>
      </Section>

      <DangerZone chatId={data.chatId} projectName={data.projectName} chatType={data.chatType} />
    </div>
  );
}

function LatestReportPanel({ report }: { report: LatestReport }) {
  // Tri-state delivery badge — explicit ✓ when the EVENTS log confirms
  // a REPORT_SENT (i.e. Telegram returned a message_id), explicit ✗ when
  // we wrote a REPORTS row but never recorded the send (most likely
  // failed before reaching Telegram), and ? for legacy rows from before
  // the EVENTS instrumentation existed.
  const deliveryBadge = (() => {
    if (report.delivered === true) {
      return {
        text: 'Delivered to Telegram',
        icon: '✓',
        cls: 'border-emerald-200 bg-emerald-50 text-emerald-800',
        tooltip: report.deliveredAt
          ? `Confirmed at ${new Date(report.deliveredAt).toISOString().slice(0, 19)} UTC${report.messageId ? ` (msg ${report.messageId})` : ''}`
          : 'Confirmed by REPORT_SENT event'
      };
    }
    if (report.delivered === false) {
      return {
        text: 'No delivery recorded',
        icon: '✗',
        cls: 'border-red-200 bg-red-50 text-red-700',
        tooltip: 'REPORT row exists but no REPORT_SENT event in the 5 min window after — likely failed before reaching Telegram'
      };
    }
    return {
      text: 'Delivery unknown (legacy)',
      icon: '?',
      cls: 'border-gray-200 bg-gray-50 text-gray-500',
      tooltip: 'Pre-2026-05-04 row — EVENTS instrumentation hadn\'t shipped yet'
    };
  })();

  return (
    <section className="mt-5 rounded-lg border border-gray-200 bg-white p-5" data-testid="latest-report-panel">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <h3 className="text-sm font-medium text-gray-700">Latest report</h3>
        <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${deliveryBadge.cls}`}
          title={deliveryBadge.tooltip}
          data-testid="latest-report-delivery-badge"
        >
          {deliveryBadge.icon} {deliveryBadge.text}
        </span>
      </div>

      <div className="mt-1 text-xs text-gray-500">
        {new Date(report.ts).toISOString().slice(0, 16).replace('T', ' ')} UTC
        {report.period && <> · period {report.period}</>}
        {report.postCount != null && <> · {report.postCount} posts</>}
        {report.userCount != null && <> · {report.userCount} users</>}
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-5">
        <div className="lg:col-span-2">
          {report.imageUrl ? (
            <a
              href={report.imageUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block"
              data-testid="latest-report-image-link"
            >
              <img
                src={report.imageUrl}
                alt="Latest report image"
                className="w-full rounded border border-gray-200 transition-transform hover:scale-[1.01]"
              />
            </a>
          ) : (
            <div className="flex h-48 items-center justify-center rounded border border-dashed border-gray-300 bg-gray-50 text-xs text-gray-400">
              No image stored
            </div>
          )}
        </div>

        <div className="lg:col-span-3">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
            AI-generated commentary
          </p>
          {report.text ? (
            <div
              className="prose prose-sm mt-2 max-h-80 overflow-auto rounded bg-gray-50 p-3 text-sm text-gray-800"
              data-testid="latest-report-text"
              // The text is HTML emitted by the bot's report renderer
              // (anchor tags around tweet links, <b> for emphasis). Trust
              // it because it's from our own pipeline; if you ever pipe
              // in user-controlled HTML this needs sanitization.
              dangerouslySetInnerHTML={{ __html: report.text }}
            />
          ) : (
            <p className="mt-2 text-sm italic text-gray-400">No text body stored.</p>
          )}
          {report.error && (
            <p className="mt-3 text-xs text-red-700">
              <span className="font-semibold">Error:</span>{' '}
              <code className="rounded bg-red-50 px-1.5 py-0.5">{report.error}</code>
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

function EngagementSummary({ engagement, chat }: { engagement: ChatEngagement; chat: ChatDetail }) {
  // Five boolean signals — green when the chat exhibits real engagement,
  // gray when not. Operator answers "is this chat using the bot" by
  // counting greens.
  const signals = [
    {
      label: 'Has filter',
      ok: engagement.hasFilter,
      detail: engagement.hasFilter ? 'Query configured' : 'Never set'
    },
    {
      label: 'Has schedule',
      ok: engagement.hasSchedule,
      detail: chat.scheduleHuman || 'No schedule rule'
    },
    {
      label: 'Reports 7d',
      ok: engagement.reportsLast7d > 0,
      detail: `${engagement.reportsLast7d} sent`
    },
    {
      label: 'Has admins',
      ok: engagement.hasAdmins,
      detail: `${chat.adminCount} designated`
    },
    {
      label: 'Has paid',
      ok: engagement.hasPaid,
      detail: chat.payment ? `${chat.tier} · ${chat.payment}` : 'FREE'
    }
  ];
  const greenCount = signals.filter((s) => s.ok).length;
  const headlineColor =
    greenCount >= 4 ? 'text-emerald-700'
    : greenCount >= 2 ? 'text-amber-700'
    : 'text-gray-500';

  return (
    <div
      className="mt-5 rounded-lg border border-gray-200 bg-white p-4"
      data-testid="engagement-summary"
    >
      <div className="flex items-baseline justify-between">
        <h3 className="text-sm font-medium text-gray-700">Engagement</h3>
        <p className={`text-sm font-semibold ${headlineColor}`}>
          {greenCount}/5 signals
        </p>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-5">
        {signals.map((s) => (
          <div
            key={s.label}
            className={`rounded border px-2.5 py-2 text-center ${
              s.ok
                ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                : 'border-gray-200 bg-gray-50 text-gray-500'
            }`}
          >
            <p className="text-[11px] font-medium">{s.ok ? '✓' : '○'} {s.label}</p>
            <p className="mt-0.5 text-[10px] opacity-80">{s.detail}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function AdminRow({ admin }: { admin: ChatAdmin }) {
  const display = admin.username
    ? `@${admin.username}`
    : admin.firstName || `#${admin.id}`;
  const reachable = admin.status !== 'unreachable';
  return (
    <li className="flex items-center justify-between rounded border border-gray-100 bg-gray-50 px-3 py-2">
      <div className="min-w-0 flex-1">
        <p
          className={`text-sm font-mono ${reachable ? 'text-gray-800' : 'text-gray-400 line-through'}`}
        >
          {display}
        </p>
        <p className="text-[11px] text-gray-500">
          ID <span className="font-mono">{admin.id}</span>
          {admin.firstName && admin.username && (
            <> · {admin.firstName}</>
          )}
          {' · '}{admin.status}
        </p>
      </div>
      {admin.username && (
        <a
          href={`https://t.me/${admin.username}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-primary-700 hover:underline"
        >
          Open on TG
        </a>
      )}
    </li>
  );
}

function SubscriptionDetails({
  subscription
}: {
  subscription: NonNullable<ChatDetail['subscription']>;
}) {
  const item = subscription.items[0];
  return (
    <div className="space-y-2 text-sm">
      <p className="text-gray-700">
        <span className="font-medium">Status:</span>{' '}
        <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs">{subscription.status}</code>
        {subscription.cancelAtPeriodEnd && (
          <span className="ml-2 text-amber-700">— cancels at period end</span>
        )}
      </p>
      {item && (
        <p className="text-gray-700">
          <span className="font-medium">Plan:</span>{' '}
          {item.nickname || item.priceId}
          {item.unitAmount != null && item.currency && (
            <> — {formatUSD(item.unitAmount / 100)} / {item.recurring}</>
          )}
        </p>
      )}
      <p className="text-gray-700">
        <span className="font-medium">Current period:</span>{' '}
        {new Date(subscription.currentPeriodStart).toISOString().slice(0, 10)} →{' '}
        {new Date(subscription.currentPeriodEnd).toISOString().slice(0, 10)}
      </p>
      <p className="text-xs font-mono text-gray-400">{subscription.id}</p>
    </div>
  );
}

function TransactionsTable({
  transactions,
  customerId
}: {
  transactions: StripeTransaction[];
  customerId: string;
}) {
  const total = transactions
    .filter((t) => t.status === 'succeeded' || t.status === 'paid')
    .reduce((sum, t) => sum + t.amount, 0);

  return (
    <div>
      <p className="mb-3 text-xs text-gray-500">
        Customer <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono">{customerId}</code> ·
        Lifetime paid:{' '}
        <span className="font-semibold text-gray-800">
          {formatUSD(total / 100)}
        </span>
      </p>
      <div className="overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead>
            <tr className="border-b border-gray-200 text-left text-gray-500">
              <th className="pb-1.5 pr-3 font-medium">When</th>
              <th className="pb-1.5 pr-3 font-medium">Kind</th>
              <th className="pb-1.5 pr-3 font-medium">Amount</th>
              <th className="pb-1.5 pr-3 font-medium">Status</th>
              <th className="pb-1.5 pr-3 font-medium">Description</th>
              <th className="pb-1.5 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {transactions.map((t) => (
              <tr key={t.id} className="border-b border-gray-50">
                <td className="py-1.5 pr-3 font-mono text-gray-700">
                  {new Date(t.createdAt).toISOString().slice(0, 10)}
                </td>
                <td className="py-1.5 pr-3">
                  <span
                    className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase ${
                      t.kind === 'charge'
                        ? 'bg-blue-50 text-blue-700'
                        : 'bg-purple-50 text-purple-700'
                    }`}
                  >
                    {t.kind}
                  </span>
                </td>
                <td className="py-1.5 pr-3 font-mono text-gray-800">
                  {formatUSD(t.amount / 100)}
                </td>
                <td className="py-1.5 pr-3 text-gray-700">
                  {t.refunded ? (
                    <span className="text-amber-700">refunded</span>
                  ) : (
                    t.status
                  )}
                </td>
                <td className="py-1.5 pr-3 text-gray-600 max-w-xs truncate" title={t.description ?? ''}>
                  {t.description || '—'}
                </td>
                <td className="py-1.5 text-right">
                  {(t.receiptUrl || t.hostedInvoiceUrl) && (
                    <a
                      href={t.receiptUrl || t.hostedInvoiceUrl || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-700 hover:underline"
                    >
                      View
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className="mt-1 text-lg font-semibold text-gray-900">{value}</p>
    </div>
  );
}

function Section({
  title,
  testid,
  className,
  children
}: {
  title: string;
  testid?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <section
      className={`rounded-lg border border-gray-200 bg-white p-5 ${className ?? ''}`}
      data-testid={testid}
    >
      <h3 className="text-sm font-medium text-gray-700">{title}</h3>
      <div className="mt-3">{children}</div>
    </section>
  );
}

/**
 * Header button — for regular Telegram groups (no public username, not a
 * supergroup) we can't construct a static link. Telegram exposes
 * createChatInviteLink which the bot can call when it's an admin; this
 * button surfaces that on demand. Returns a 1-hour, member-limit-1 link
 * the admin can click once to enter the chat.
 */
function InviteLinkButton({
  chatId,
  botMembership,
}: {
  chatId: string;
  botMembership: string | null;
}) {
  const enabled = botMembership === 'administrator' || botMembership === 'creator';
  const mutation = useMutation({ mutationFn: () => generateInviteLink(chatId) });

  if (!enabled) {
    return (
      <span
        className="text-xs italic text-gray-400"
        title="Bot needs to be admin (with can_invite_users) to generate an invite link."
      >
        no link available
      </span>
    );
  }

  if (mutation.isSuccess && mutation.data?.ok && mutation.data.inviteLink) {
    return (
      <a
        href={mutation.data.inviteLink}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 rounded-md border border-sky-200 bg-sky-50 px-2.5 py-1 text-xs font-medium text-sky-700 hover:bg-sky-100"
        title={`Expires ${new Date(mutation.data.expiresAt!).toISOString().slice(11, 16)} UTC · single-use`}
      >
        Open in Telegram ↗
      </a>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending}
        className="inline-flex items-center gap-1 rounded-md border border-sky-200 bg-white px-2.5 py-1 text-xs font-medium text-sky-700 hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-50"
        title="Generate a 1-hour, single-use Telegram invite link via the bot."
      >
        {mutation.isPending ? '…generating' : 'Get invite link'}
      </button>
      {(mutation.isError || (mutation.isSuccess && !mutation.data?.ok)) && (
        <span className="text-xs text-red-700">
          {(mutation.error as Error | undefined)?.message ?? mutation.data?.error ?? 'failed'}
        </span>
      )}
    </div>
  );
}

/**
 * Header button — kicks off the Tweets-Fetch state machine for this chat,
 * the same path /setup → Run report uses inside Telegram. Disabled when
 * no filter is configured (the SF would no-op and DM admins).
 */
function RunReportButton({ chatId, hasFilter }: { chatId: string; hasFilter: boolean }) {
  const mutation = useMutation({ mutationFn: () => runReport(chatId) });
  const tooltip = hasFilter
    ? 'Trigger Tweets-Fetch for this chat now. Same path as /setup → Run report.'
    : 'Filter not configured — the bot would have nothing to fetch.';
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => mutation.mutate()}
        disabled={!hasFilter || mutation.isPending}
        title={tooltip}
        className="inline-flex items-center gap-1 rounded-md border border-emerald-300 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
        data-testid="run-report"
      >
        {mutation.isPending ? '…running' : '▶ Run report'}
      </button>
      {mutation.isSuccess && mutation.data?.ok && (
        <span className="text-xs text-emerald-700" data-testid="run-report-success">
          ✓ started — check the chat in 1–3 min
        </span>
      )}
      {(mutation.isError || (mutation.isSuccess && !mutation.data?.ok)) && (
        <span className="text-xs text-red-700">
          {(mutation.error as Error | undefined)?.message ?? mutation.data?.error ?? 'failed'}
        </span>
      )}
    </div>
  );
}

/**
 * Inline call-to-action shown inside the Schedule section when the chat
 * has no cron rule. Picks a randomized daily time (server-side) so two
 * back-to-back reactivations don't pile onto the same minute and sets
 * the rolling 7-day window as the safe default.
 */
function ReactivateBanner({ chatId }: { chatId: string }) {
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => reactivateChat(chatId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['chat', chatId] });
    },
  });

  return (
    <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm">
      <p className="text-emerald-800">
        <strong>No schedule.</strong> Reactivate creates a daily fire at a randomized time
        (so it doesn't collide with other chats) and a rolling 7-day window.
      </p>
      <button
        type="button"
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending}
        className="mt-3 rounded-md border border-emerald-300 bg-white px-3 py-1.5 text-sm font-medium text-emerald-700 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
        data-testid="reactivate-chat"
      >
        {mutation.isPending ? 'Scheduling…' : '▶ Reactivate this chat'}
      </button>
      {mutation.isSuccess && mutation.data?.ok && (
        <p className="mt-2 text-xs text-emerald-700">
          Scheduled <code className="rounded bg-emerald-100 px-1 font-mono">{mutation.data.cron}</code> · period <code className="rounded bg-emerald-100 px-1 font-mono">{mutation.data.period}</code>.
        </p>
      )}
      {(mutation.isError || (mutation.isSuccess && !mutation.data?.ok)) && (
        <p className="mt-2 text-xs text-red-700">
          {(mutation.error as Error | undefined)?.message ?? mutation.data?.error ?? 'Unknown error'}
        </p>
      )}
    </div>
  );
}
